import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/react-hooks';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  CloseButton,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import useHeaderComponentsMutation from '../../../Hooks/useHeaderComponentsMutation';
import { EVENT_TITLE } from '../../../Constants/AppConstants';
import { ADD_SPIRIT_POINTS, ADD_SPIRIT_POINTS_TO_SCHOOL } from '../../../GraphQL/Mutations';
import { SPIRIT_POINT_TEMPLATE } from '../../../GraphQL/Types';
import { Provider } from '../../../Chakra/provider';

const FETCH_AWARD_DATA = gql`
  query awardSpiritPointsData($eventId: Int!, $eventIdStr: ID!) {
    fetchEventDetail(id: $eventId) {
      id
      title
      spiritPointTemplates {
        ${SPIRIT_POINT_TEMPLATE}
      }
    }
    eventSchoolTeams(eventId: $eventIdStr) {
      id
      school {
        id
        name
      }
    }
  }
`;

const isNumeric = (str) => /^-?\d+$/.test(str);

const TemplateListItem = ({ template, isSelected, onClick }) => (
  <Box
    as="button"
    aria-pressed={isSelected}
    onClick={onClick}
    w="100%"
    textAlign="left"
    px={3}
    py={2}
    borderRadius="md"
    borderWidth={1}
    borderColor={isSelected ? 'yellow.400' : 'gray.200'}
    bg={isSelected ? 'yellow.50' : 'white'}
    _hover={{ borderColor: 'yellow.300', bg: 'yellow.50' }}
    _focus={{ outline: 'none', boxShadow: 'none' }}
    _focusVisible={{ outline: 'none', boxShadow: 'none' }}
  >
    <HStack justify="space-between" spacing={2}>
      <Text
        fontWeight={isSelected ? 'semibold' : 'normal'}
        fontSize="sm"
        noOfLines={1}
      >
        {template.title}
      </Text>
      <Badge
        colorScheme={template.amount >= 0 ? 'green' : 'red'}
        fontSize="xs"
        flexShrink={0}
      >
        {template.amount >= 0 ? '+' : ''}{template.amount}
      </Badge>
    </HStack>
  </Box>
);

const CustomTemplateItem = ({ isSelected, onSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');

  const isValid = description.trim().length > 0 && isNumeric(amount);

  const handleApply = () => {
    if (!isValid) return;
    onSelect({ title: 'Custom', description: description.trim(), amount: parseInt(amount, 10) });
  };

  return (
    <Box
      w="100%"
      borderRadius="md"
      borderWidth={1}
      borderColor={isSelected ? 'yellow.400' : 'gray.200'}
      bg={isSelected ? 'yellow.50' : 'white'}
      overflow="hidden"
    >
      <Box
        as="button"
        aria-expanded={isExpanded}
        w="100%"
        textAlign="left"
        px={3}
        py={2}
        onClick={() => setIsExpanded((v) => !v)}
        _focus={{ outline: 'none', boxShadow: 'none' }}
        _focusVisible={{ outline: 'none', boxShadow: 'none' }}
      >
        <HStack justify="space-between">
          <Text fontWeight="normal" fontSize="sm" color="gray.600">Custom entry</Text>
          <Text fontSize="xs" color="gray.400" aria-hidden="true">{isExpanded ? '▲' : '▼'}</Text>
        </HStack>
      </Box>

      {isExpanded && (
        <VStack align="stretch" spacing={3} px={3} pb={3}>
          <FormControl isRequired>
            <FormLabel fontSize="xs" mb={1}>Description</FormLabel>
            <Input
              size="sm"
              bg="white"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Reason for points"
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel fontSize="xs" mb={1}>Points</FormLabel>
            <NumberInput size="sm" value={amount} onChange={(val) => setAmount(val)} maxW="120px">
              <NumberInputField bg="white" placeholder="e.g. 5 or -10" />
            </NumberInput>
          </FormControl>
          <Button
            size="sm"
            colorScheme="yellow"
            onClick={handleApply}
            isDisabled={!isValid}
            alignSelf="flex-start"
          >
            Apply Custom
          </Button>
        </VStack>
      )}
    </Box>
  );
};

const StudentAwardTab = ({ eventId, template, onSuccess, onError }) => {
  const [userCode, setUserCode] = useState('');
  const toast = useToast();

  const [addPoints, { loading }] = useMutation(ADD_SPIRIT_POINTS, {
    onCompleted: (data) => {
      const eu = data.addSpiritPoints.eventUser;
      const name = `${eu.fetchUser.firstname} ${eu.fetchUser.lastname}`;
      toast({
        title: `Awarded ${template.amount >= 0 ? '+' : ''}${template.amount} pts to ${name}`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      setUserCode('');
      onSuccess();
    },
    onError: (err) => {
      onError(err.graphQLErrors?.[0]?.message ?? err.message);
    },
  });

  const handleSubmit = () => {
    if (userCode.trim().length !== 5) return;
    addPoints({
      variables: {
        input: {
          userCode: userCode.trim().toUpperCase(),
          eventId,
          amount: template.amount,
          description: template.description || template.title,
        },
      },
    });
  };

  return (
    <VStack align="stretch" spacing={4}>
      <FormControl>
        <FormLabel fontSize="sm">Student badge code</FormLabel>
        <HStack>
          <Input
            placeholder="e.g. AB1C2"
            value={userCode}
            onChange={(e) => setUserCode(e.target.value.toUpperCase())}
            maxLength={5}
            fontFamily="mono"
            letterSpacing="wider"
            bg="white"
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
          />
          <Button
            colorScheme="yellow"
            onClick={handleSubmit}
            isLoading={loading}
            isDisabled={userCode.trim().length !== 5}
            minW="100px"
          >
            Award
          </Button>
        </HStack>
        <Text fontSize="xs" color="gray.500" mt={1}>
          5-character code printed on the student&apos;s badge
        </Text>
      </FormControl>
    </VStack>
  );
};

const SchoolAwardTab = ({ eventId, template, teams, onSuccess, onError }) => {
  const [schoolId, setSchoolId] = useState('');
  const toast = useToast();

  const [addPoints, { loading }] = useMutation(ADD_SPIRIT_POINTS_TO_SCHOOL, {
    onCompleted: (data) => {
      const { name } = data.addSpiritPointsToSchool.team.school;
      toast({
        title: `Awarded ${template.amount >= 0 ? '+' : ''}${template.amount} pts to ${name}`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      setSchoolId('');
      onSuccess();
    },
    onError: (err) => {
      onError(err.graphQLErrors?.[0]?.message ?? err.message);
    },
  });

  const handleSubmit = () => {
    if (!schoolId) return;
    addPoints({
      variables: {
        input: {
          eventId,
          schoolId,
          amount: template.amount,
          description: template.description || template.title,
        },
      },
    });
  };

  const sortedTeams = [...teams].sort((a, b) => a.school.name.localeCompare(b.school.name));

  return (
    <VStack align="stretch" spacing={4}>
      <FormControl>
        <FormLabel fontSize="sm">School</FormLabel>
        <HStack>
          <Select
            placeholder="Select school..."
            value={schoolId}
            onChange={(e) => setSchoolId(e.target.value)}
            bg="white"
          >
            {sortedTeams.map((team) => (
              <option key={team.school.id} value={team.school.id}>
                {team.school.name}
              </option>
            ))}
          </Select>
          <Button
            colorScheme="yellow"
            onClick={handleSubmit}
            isLoading={loading}
            isDisabled={!schoolId}
            minW="100px"
          >
            Award
          </Button>
        </HStack>
        <Text fontSize="xs" color="gray.500" mt={1}>
          Awards points to all students in the selected school&apos;s team
        </Text>
      </FormControl>
    </VStack>
  );
};

const AwardPanel = ({ template, eventId, teams, onSuccess, onError }) => {
  if (!template) {
    return (
      <Flex
        h={{ base: 'auto', md: '200px' }}
        align="center"
        justify="center"
        borderWidth={1}
        borderRadius="md"
        borderColor="gray.200"
        borderStyle="dashed"
        bg="gray.50"
        p={6}
      >
        <Text color="gray.400" fontSize="sm" textAlign="center">
          Select a template on the left to award points
        </Text>
      </Flex>
    );
  }

  const amountDisplay = `${template.amount >= 0 ? '+' : ''}${template.amount} pts`;
  const isDeduction = template.amount < 0;

  return (
    <Box borderWidth={1} borderRadius="md" borderColor="yellow.300" bg="white" overflow="hidden">
      <Box px={4} py={3} bg="yellow.50" borderBottomWidth={1} borderBottomColor="yellow.200">
        <HStack justify="space-between">
          <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>{template.title}</Text>
          <Badge colorScheme={isDeduction ? 'red' : 'green'} flexShrink={0}>
            {amountDisplay}
          </Badge>
        </HStack>
        {template.description && template.description !== template.title && (
          <Text fontSize="xs" color="gray.600" mt={0.5}>{template.description}</Text>
        )}
      </Box>

      <Box p={4}>
        <Tabs colorScheme="yellow" variant="enclosed" size="sm">
          <TabList>
            <Tab>Student</Tab>
            <Tab>School</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0} pb={0}>
              <StudentAwardTab
                eventId={eventId}
                template={template}
                onSuccess={onSuccess}
                onError={onError}
              />
            </TabPanel>
            <TabPanel px={0} pb={0}>
              <SchoolAwardTab
                eventId={eventId}
                template={template}
                teams={teams}
                onSuccess={onSuccess}
                onError={onError}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
};

export const AwardSpiritPoints = () => {
  const { id } = useParams();
  const eventId = parseInt(id, 10);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [search, setSearch] = useState('');

  const { data, loading } = useQuery(FETCH_AWARD_DATA, {
    variables: { eventId, eventIdStr: id },
    fetchPolicy: 'no-cache',
  });

  const eventTitle = data?.fetchEventDetail?.title || '';
  const allTemplates = [...(data?.fetchEventDetail?.spiritPointTemplates || [])].sort(
    (a, b) => b.amount - a.amount,
  );
  const teams = data?.eventSchoolTeams || [];

  const filteredTemplates = search.trim()
    ? allTemplates.filter((t) =>
        t.title.toLowerCase().includes(search.trim().toLowerCase())
        || (t.description || '').toLowerCase().includes(search.trim().toLowerCase()),
      )
    : allTemplates;

  useHeaderComponentsMutation({
    title: 'AWARD SPIRIT POINTS',
    backLink: `/event-manage/${id}`,
    components: [{ key: EVENT_TITLE, value: eventTitle }],
  });

  const onError = (msg) => {
    setErrorMessage(msg);
    window.scrollTo(0, 0);
  };

  const onSuccess = () => {
    setErrorMessage('');
  };

  if (loading) {
    return (
      <Provider>
        <Flex w="100%" h="200px" justifyContent="center" alignItems="center">
          <Spinner size="xl" />
        </Flex>
      </Provider>
    );
  }

  const hasTemplates = allTemplates.length > 0;

  return (
    <Provider>
      <Box p={6}>
        {errorMessage && (
          <Alert status="error" mb={4}>
            <AlertIcon />
            <Box flex="1">{errorMessage}</Box>
            <CloseButton onClick={() => setErrorMessage('')} />
          </Alert>
        )}

        {!hasTemplates && (
          <Alert status="info" mb={4}>
            <AlertIcon />
            No templates configured for this event. Set up templates in the{' '}
            <Box
              as="a"
              href={`/events/${id}/spirit-points-manager`}
              ml={1}
              fontWeight="semibold"
              textDecoration="underline"
            >
              Spirit Points Manager
            </Box>
            .
          </Alert>
        )}

        <Grid
          templateColumns={{ base: '1fr', md: '280px 1fr' }}
          gap={6}
          alignItems="start"
        >
          {/* Left: template list */}
          <GridItem>
            <Flex direction="column">
              <Heading size="sm" mb={3} color="gray.700">Templates</Heading>

              {allTemplates.length > 5 && (
                <FormControl mb={2}>
                  <Input
                    size="sm"
                    placeholder="Filter templates..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    bg="white"
                  />
                </FormControl>
              )}

              <Box
                maxH={{ base: 'none', md: '45vh' }}
                overflowY={{ base: 'visible', md: 'auto' }}
                pr={{ md: 1 }}
              >
                <VStack align="stretch" spacing={1}>
                  {filteredTemplates.length === 0 && search ? (
                    <Text fontSize="sm" color="gray.400" px={2} py={3}>
                      No templates match &ldquo;{search}&rdquo;
                    </Text>
                  ) : (
                    filteredTemplates.map((template) => (
                      <TemplateListItem
                        key={template.id}
                        template={template}
                        isSelected={selectedTemplate?.id === template.id}
                        onClick={() => setSelectedTemplate(template)}
                      />
                    ))
                  )}
                </VStack>
              </Box>

              <Box borderTopWidth={1} borderColor="gray.200" mt={3} pt={3} flexShrink={0}>
                <Heading size="xs" mb={2} color="gray.600">Custom</Heading>
                <CustomTemplateItem
                  isSelected={selectedTemplate?.title === 'Custom'}
                  onSelect={setSelectedTemplate}
                />
              </Box>
            </Flex>
          </GridItem>

          {/* Right: entry panel */}
          <GridItem>
            <Box position={{ md: 'sticky' }} top={{ md: '16px' }}>
              <Heading size="sm" mb={3} color="gray.700">Entry</Heading>
              <AwardPanel
                template={selectedTemplate}
                eventId={eventId}
                teams={teams}
                onSuccess={onSuccess}
                onError={onError}
              />
            </Box>
          </GridItem>
        </Grid>
      </Box>
    </Provider>
  );
};
